import React, { useEffect, useMemo, useRef } from "react";

function percent(value) {
  const number = Number(value);
  if (!Number.isFinite(number)) return null;
  return Math.round(Math.max(0, Math.min(1, number)) * 100);
}

function dateLabel(value) {
  if (!value) return null;
  const date = new Date(value);
  if (!Number.isFinite(date.getTime())) return null;
  return date.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
}

function sourceTitle(item, index) {
  return item?.title || item?.publisher || `Source ${index + 1}`;
}

export default function RealityProofPanel({
  open,
  summary,
  proof,
  loading = false,
  error = "",
  onClose,
}) {
  const panelRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const previous = document.body.style.overflow;
    const previousFocus = document.activeElement;
    document.body.style.overflow = "hidden";
    window.requestAnimationFrame(() => panelRef.current?.focus());
    const onKey = event => {
      if (event.key === "Escape") onClose?.();
    };
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = previous;
      window.removeEventListener("keydown", onKey);
      if (previousFocus && typeof previousFocus.focus === "function") previousFocus.focus();
    };
  }, [open, onClose]);

  const resolved = proof || summary || null;
  const ri = resolved?.realityIndex;
  const coverage = percent(resolved?.evidenceCoverage);
  const sources = proof?.evidence || [];
  const domains = proof?.domains || summary?.domains || [];
  const explanations = proof?.explanations || [];
  const sourceCount = proof?.evidenceSummary?.independentLineages ?? summary?.sourceCount ?? 0;

  const label = useMemo(() => {
    if (!resolved) return "Unverified";
    return resolved.label
      || (resolved.classification
        ? String(resolved.classification).replace(/_/g, " ").replace(/^./, value => value.toUpperCase())
        : "Unverified");
  }, [resolved]);

  if (!open) return null;

  return (
    <div
      className="reality-proof-overlay"
      onMouseDown={event => {
        if (event.target === event.currentTarget) onClose?.();
      }}
    >
      <aside
        ref={panelRef}
        tabIndex={-1}
        className="reality-proof-panel"
        role="dialog"
        aria-modal="true"
        aria-labelledby="reality-proof-title"
      >
        <header className="reality-proof-panel__head">
          <div>
            <p className="reality-kicker">Reality Proof</p>
            <h2 id="reality-proof-title">Why this Reality is {label.toLowerCase()}.</h2>
          </div>
          <button type="button" className="reality-proof-close" onClick={onClose} aria-label="Close Reality Proof">×</button>
        </header>

        <div className="reality-proof-index">
          <div>
            <span>Reality Index</span>
            <strong>{ri == null ? "RI —" : `RI ${ri}`}</strong>
            <em>{label}</em>
          </div>
          <p>
            RI describes how strongly this Reality is grounded in available evidence.
            It is not a quality, popularity, safety or design score.
          </p>
        </div>

        {loading ? (
          <div className="reality-proof-loading" role="status">Loading public proof…</div>
        ) : error ? (
          <div className="reality-proof-error" role="status">
            <strong>Proof details are not available.</strong>
            <span>{error}</span>
          </div>
        ) : (
          <>
            <section className="reality-proof-overview" aria-label="Grounding overview">
              <div><span>Evidence coverage</span><strong>{coverage == null ? "—" : `${coverage}%`}</strong></div>
              <div><span>Independent sources</span><strong>{sourceCount || "—"}</strong></div>
              <div><span>Public sources</span><strong>{proof?.evidenceSummary?.publicRecords ?? summary?.publicEvidenceCount ?? "—"}</strong></div>
              <div><span>Policy</span><strong>{proof?.policyVersion || "Grounding V1"}</strong></div>
            </section>

            {domains.length ? (
              <section className="reality-proof-section">
                <div className="reality-proof-section__title">
                  <span>By domain</span>
                  <small>Lower RI means stronger grounding.</small>
                </div>
                <div className="reality-proof-domains">
                  {domains.map(domain => (
                    <div className="reality-proof-domain" key={domain.id || domain.label}>
                      <span>{domain.label || domain.id}</span>
                      <strong>{domain.realityIndex == null ? "RI —" : `RI ${domain.realityIndex}`}</strong>
                      <em>{String(domain.classification || "unverified").replace(/_/g, " ")}</em>
                    </div>
                  ))}
                </div>
              </section>
            ) : null}

            {explanations.length ? (
              <section className="reality-proof-section">
                <div className="reality-proof-section__title"><span>Why</span></div>
                <div className="reality-proof-explanations">
                  {explanations.map(item => (
                    <p key={item.code || item.message}>{item.message}</p>
                  ))}
                </div>
              </section>
            ) : null}

            <section className="reality-proof-section">
              <div className="reality-proof-section__title">
                <span>Public evidence</span>
                <small>{sources.length ? `${sources.length} available to inspect` : "No public evidence records available"}</small>
              </div>

              {sources.length ? (
                <div className="reality-proof-sources">
                  {sources.map((item, index) => {
                    const observed = dateLabel(item.observedAt || item.retrievedAt);
                    return (
                      <article className="reality-proof-source" key={item.id || index}>
                        <div>
                          <strong>{sourceTitle(item, index)}</strong>
                          <span>
                            {[item.evidenceType, item.authority, observed].filter(Boolean).join(" · ")}
                          </span>
                        </div>
                        {item.sourceUrl ? (
                          <a href={item.sourceUrl} target="_blank" rel="noreferrer">Open source ↗</a>
                        ) : (
                          <span className="reality-proof-source__private">Public record</span>
                        )}
                      </article>
                    );
                  })}
                </div>
              ) : (
                <p className="reality-proof-note">
                  This Reality may still use restricted or private evidence internally. Only evidence explicitly approved for public disclosure appears here.
                </p>
              )}
            </section>
          </>
        )}

        <footer className="reality-proof-foot">
          <span>Reality Index explains grounding against evidence DoAR currently has.</span>
          <button type="button" onClick={onClose}>Done</button>
        </footer>
      </aside>
    </div>
  );
}
